import {
  formatReceiptDate,
  formatReceiptTime,
  formatThermalAmount,
  formatThermalCurrencyParts,
  wrapText
} from './thermalReceiptFormatter';
import { getReceiptLabel, isReceiptRtl } from '../../utils/receiptLabels';

function t(key, language) {
  return getReceiptLabel(key, language);
}

export function escapeThermalHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function moneyValue(amount, config) {
  return {
    type: 'money',
    ...formatThermalCurrencyParts(amount, config)
  };
}

function renderMoneyHtml(parts, { emphasis = false } = {}) {
  if (!parts || parts.type !== 'money') {
    return escapeThermalHtml(parts);
  }
  const dir = parts.rtl ? 'rtl' : 'ltr';
  const emphasisClass = emphasis ? ' tr-money--emphasis' : '';
  return `<span class="tr-money${emphasisClass}" dir="${dir}"><span class="tr-money__amount" dir="ltr">${escapeThermalHtml(parts.amount)}</span><span class="tr-money__currency">${escapeThermalHtml(parts.currency)}</span></span>`;
}

/**
 * Printer-neutral receipt structure for HTML preview and silent print.
 */
export function buildThermalReceiptLayoutData(viewModel, config) {
  const language = config.language || 'fr';
  const rtl = isReceiptRtl(language);
  const compact = config.compactLayout === true;
  const locale = config.locale || 'fr-FR';

  const metaRows = [];
  metaRows.push({
    label: `${t('receiptNo', language)}:`,
    value: viewModel.receipt.number,
    ltr: true
  });

  if (compact) {
    const dateTime = `${formatReceiptDate(viewModel.receipt.date, locale)} ${formatReceiptTime(viewModel.receipt.date, locale)}`;
    metaRows.push({ label: `${t('date', language)}:`, value: dateTime, ltr: true });
  } else {
    metaRows.push({
      label: `${t('date', language)}:`,
      value: formatReceiptDate(viewModel.receipt.date, locale),
      ltr: true
    });
    metaRows.push({
      label: `${t('time', language)}:`,
      value: formatReceiptTime(viewModel.receipt.date, locale),
      ltr: true
    });
  }

  if (viewModel.cashier.show) {
    metaRows.push({ label: `${t('cashier', language)}:`, value: viewModel.cashier.name, ltr: false });
  }
  if (viewModel.customer.show && viewModel.customer.name) {
    metaRows.push({ label: t('customer', language), value: viewModel.customer.name, ltr: false });
  }
  if (viewModel.location.show && viewModel.location.name) {
    metaRows.push({ label: t('location', language), value: viewModel.location.name, ltr: false });
  }

  // Arabic: slightly wider price/total so amounts stay readable on 80mm paper
  const nameColWidth = rtl ? 14 : 20;
  const itemRows = viewModel.items.map((item) => {
    const qty = String(Number(item.quantity) || 0);
    const unitPrice = formatThermalAmount(item.unitPrice, config);
    const total = formatThermalAmount(item.lineTotal, config);
    return {
      nameLines: wrapText(item.name, nameColWidth),
      qty,
      unitPrice,
      total
    };
  });

  const summaryRows = [];
  if (viewModel.flags.showItemCount) {
    summaryRows.push({
      label: `${t('items', language)}:`,
      value: String(viewModel.totals.itemCount),
      ltr: true
    });
  }

  if (viewModel.totals.discount > 0) {
    summaryRows.push({
      label: t('discount', language),
      value: moneyValue(-viewModel.totals.discount, config)
    });
  }

  if (viewModel.totals.showVat && viewModel.totals.tax > 0) {
    summaryRows.push({
      label: `${t('vat', language)}:`,
      value: moneyValue(viewModel.totals.tax, config)
    });
  }

  summaryRows.push({
    label: `${t('totalThermal', language)}:`,
    value: moneyValue(viewModel.totals.total, config),
    emphasis: true
  });

  const paymentRows = [
    { label: t('payment', language), value: viewModel.payment.method, ltr: false }
  ];
  if (viewModel.payment.isCash) {
    paymentRows.push({
      label: `${t('thermalCashPaid', language)}:`,
      value: moneyValue(viewModel.payment.cashAmount, config)
    });
    paymentRows.push({
      label: `${t('thermalChange', language)}:`,
      value: moneyValue(viewModel.payment.change, config)
    });
  }

  return {
    dir: rtl ? 'rtl' : 'ltr',
    language,
    store: viewModel.store,
    title: viewModel.receipt.title || t('receiptTitleClient', language),
    showNif: config.showNif !== false && viewModel.flags.showNif && viewModel.store.nif,
    metaRows,
    itemHeaders: {
      article: t('article', language),
      qty: rtl ? t('thermalColQty', language) : t('qty', language),
      price: rtl ? t('thermalColPrice', language) : t('price', language),
      total: rtl ? t('thermalColTotal', language) : t('total', language)
    },
    itemRows,
    summaryRows,
    paymentRows,
    footer: viewModel.footerMessage,
    showLogo: viewModel.flags.showLogo && viewModel.store.logoUrl,
    showBarcode: viewModel.flags.showBarcode && viewModel.receipt.barcode,
    barcode: viewModel.receipt.barcode,
    showQr: viewModel.flags.showQrCode && viewModel.receipt.qrPayload
  };
}

function renderKvValue(value, { emphasis = false, ltr = false } = {}) {
  if (value && typeof value === 'object' && value.type === 'money') {
    return renderMoneyHtml(value, { emphasis });
  }
  return `<span class="${ltr ? 'tr-ltr' : ''}">${escapeThermalHtml(value)}</span>`;
}

function renderKvRows(rows) {
  return rows
    .map(
      (row) => `<div class="tr-kv${row.emphasis ? ' tr-kv--emphasis' : ''}">
  <span class="tr-kv__label">${escapeThermalHtml(row.label)}</span>
  <span class="tr-kv__value">${renderKvValue(row.value, { emphasis: row.emphasis, ltr: row.ltr })}</span>
</div>`
    )
    .join('\n');
}

function renderItemRows(itemRows) {
  const parts = [];
  for (const item of itemRows) {
    const lines = item.nameLines.length ? item.nameLines : [''];
    if (lines.length > 1) {
      for (let i = 0; i < lines.length - 1; i += 1) {
        parts.push(
          `<tr class="tr-item tr-item--name"><td colspan="4" class="tr-col-name">${escapeThermalHtml(lines[i])}</td></tr>`
        );
      }
    }
    parts.push(`<tr class="tr-item">
  <td class="tr-col-name">${escapeThermalHtml(lines[lines.length - 1])}</td>
  <td class="tr-col-qty tr-ltr">${escapeThermalHtml(item.qty)}</td>
  <td class="tr-col-price tr-num">${escapeThermalHtml(item.unitPrice)}</td>
  <td class="tr-col-total tr-num">${escapeThermalHtml(item.total)}</td>
</tr>`);
  }
  return parts.join('\n');
}

export function renderThermalReceiptBodyHtml(layoutData) {
  const { dir, store, title, showNif, itemHeaders } = layoutData;

  const logoBlock = layoutData.showLogo
    ? `<div class="tr-logo-wrap"><img class="tr-logo" src="${escapeThermalHtml(store.logoUrl)}" alt="" /></div>`
    : '';

  const nifBlock = showNif
    ? `<div class="tr-center tr-ltr">${escapeThermalHtml(`${t('nif', layoutData.language)}: ${store.nif}`)}</div>`
    : '';

  const barcodeBlock = layoutData.showBarcode
    ? `<div class="tr-barcode tr-ltr">*${escapeThermalHtml(layoutData.barcode)}*</div>`
    : '';

  const qrBlock = layoutData.showQr ? '<div class="tr-center">[QR]</div>' : '';

  return `<div class="tr-receipt" dir="${dir}">
${logoBlock}
<div class="tr-center tr-store-name">${escapeThermalHtml(String(store.name || '').toUpperCase())}</div>
${store.address ? `<div class="tr-center">${escapeThermalHtml(store.address)}</div>` : ''}
${store.phone ? `<div class="tr-center tr-ltr">${escapeThermalHtml(store.phone)}</div>` : ''}
${nifBlock}
<div class="tr-title">${escapeThermalHtml(title)}</div>
${renderKvRows(layoutData.metaRows)}
<hr class="tr-sep" />
<table class="tr-items" dir="${dir}">
  <thead>
    <tr>
      <th class="tr-col-name">${escapeThermalHtml(itemHeaders.article)}</th>
      <th class="tr-col-qty">${escapeThermalHtml(itemHeaders.qty)}</th>
      <th class="tr-col-price">${escapeThermalHtml(itemHeaders.price)}</th>
      <th class="tr-col-total">${escapeThermalHtml(itemHeaders.total)}</th>
    </tr>
  </thead>
  <tbody>
${renderItemRows(layoutData.itemRows)}
  </tbody>
</table>
<hr class="tr-sep" />
${renderKvRows(layoutData.summaryRows)}
<hr class="tr-sep" />
${renderKvRows(layoutData.paymentRows)}
<hr class="tr-sep" />
<div class="tr-center tr-footer">${escapeThermalHtml(layoutData.footer)}</div>
${barcodeBlock}
${qrBlock}
</div>`;
}

/** Inline CSS for print window (single source — screen preview uses same HTML in iframe). */
export const THERMAL_RECEIPT_LAYOUT_CSS = `
.tr-receipt { width: 100%; }
.tr-center { text-align: center; width: 100%; }
.tr-store-name { font-weight: 700; margin: 1px 0; }
.tr-title { font-weight: 700; text-align: center; margin: 3px 0 2px; }
.tr-logo-wrap { text-align: center; margin: 0 0 1mm; }
.tr-logo { max-width: 22mm; max-height: 10mm; object-fit: contain; }
.tr-sep { border: none; border-top: 1px dashed #000; margin: 3px 0; height: 0; }
.tr-kv { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; margin: 1px 0; }
.tr-kv--emphasis { font-weight: 700; }
.tr-kv__label { flex: 1 1 auto; min-width: 0; }
.tr-kv__value { flex: 0 0 auto; max-width: 58%; text-align: end; }
.tr-ltr { direction: ltr; unicode-bidi: isolate; }
.tr-money {
  display: inline-flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0.25em;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tr-money[dir="ltr"] { direction: ltr; }
.tr-money[dir="rtl"] { direction: rtl; }
.tr-money__amount { direction: ltr; unicode-bidi: isolate; }
.tr-money__currency { white-space: nowrap; }
.tr-money--emphasis { font-weight: 700; }
.tr-num {
  direction: ltr;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tr-items { width: 100%; table-layout: fixed; border-collapse: collapse; margin: 2px 0; font: inherit; }
.tr-items th, .tr-items td { padding: 1px 1px; vertical-align: top; overflow-wrap: anywhere; word-break: break-word; }
.tr-col-name { width: 44%; text-align: start; }
.tr-col-qty { width: 12%; text-align: center; }
.tr-col-price { width: 18%; text-align: end; }
.tr-col-total { width: 26%; text-align: end; }
.tr-col-qty, .tr-col-price, .tr-col-total {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tr-items th {
  font-weight: 700;
  vertical-align: bottom;
  line-height: 1.15;
}
.tr-item--name td { padding-bottom: 0; }
.tr-barcode { margin-top: 2px; text-align: center; letter-spacing: 0.5px; }
.tr-footer { margin-top: 2px; }
html[dir="rtl"] .tr-items,
.thermal-print-area--layout[dir="rtl"] .tr-items { direction: rtl; }
html[dir="rtl"] .tr-items th,
.thermal-print-area--layout[dir="rtl"] .tr-items th {
  font-size: 0.78em;
  line-height: 1.25;
  padding: 2px 2px 3px;
  letter-spacing: 0;
  font-weight: 700;
}
html[dir="rtl"] .tr-items td,
.thermal-print-area--layout[dir="rtl"] .tr-items td {
  font-size: 0.9em;
  line-height: 1.25;
  padding: 2px 2px;
}
html[dir="rtl"] .tr-col-name,
.thermal-print-area--layout[dir="rtl"] .tr-col-name { width: 32%; text-align: right; }
html[dir="rtl"] .tr-col-qty,
.thermal-print-area--layout[dir="rtl"] .tr-col-qty { width: 12%; text-align: center; }
html[dir="rtl"] .tr-col-price,
.thermal-print-area--layout[dir="rtl"] .tr-col-price { width: 26%; text-align: left; }
html[dir="rtl"] .tr-col-total,
.thermal-print-area--layout[dir="rtl"] .tr-col-total { width: 30%; text-align: left; }
html[dir="rtl"] .tr-col-qty,
html[dir="rtl"] .tr-col-price,
html[dir="rtl"] .tr-col-total,
.thermal-print-area--layout[dir="rtl"] .tr-col-qty,
.thermal-print-area--layout[dir="rtl"] .tr-col-price,
.thermal-print-area--layout[dir="rtl"] .tr-col-total {
  direction: ltr;
  unicode-bidi: isolate;
}
html[dir="rtl"] .tr-col-price,
html[dir="rtl"] .tr-col-total,
.thermal-print-area--layout[dir="rtl"] .tr-col-price,
.thermal-print-area--layout[dir="rtl"] .tr-col-total {
  font-weight: 600;
  letter-spacing: 0.01em;
}
html[dir="rtl"] .tr-kv,
.thermal-print-area--layout[dir="rtl"] .tr-kv { gap: 4px; }
html[dir="rtl"] .tr-kv__label,
.thermal-print-area--layout[dir="rtl"] .tr-kv__label { text-align: right; font-size: 0.92em; }
html[dir="rtl"] .tr-kv__value,
.thermal-print-area--layout[dir="rtl"] .tr-kv__value { max-width: 55%; text-align: left; }
html[dir="rtl"] .tr-title,
.thermal-print-area--layout[dir="rtl"] .tr-title { font-size: 0.95em; line-height: 1.25; }
`;
